import {
  Accordion,
  AccordionButton,
  AccordionItem,
  Box,
  Flex,
  Hide,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  Button,
  MotionAccordionPanel,
  MotionBox,
  SecondaryPlpNav,
  TextLink,
} from '@time-line/components/common';
import { Minus, Plus } from '@time-line/components/icons';
import { useTheme } from '@time-line/components/theme';
import type { Nav } from '@time-line/components/types';
import { CountrySelector } from 'components/localisation/CountrySelector';
import { SiteSearchMobile } from 'components/siteSearch/SiteSearchMobile';
import { useGlobals } from 'hooks/useGlobals';
import { env } from 'lib/constants/env';
import { AnimatePresence } from 'motion/react';
import NextLink from 'next/link';
import { RemoveScroll } from 'react-remove-scroll';
import { useWindowSize } from 'react-use';
import {
  getExternalLinkProps,
  getShowIconProps,
} from 'utils/getExternalLinkProps';
import { getTrackingId } from 'utils/getTrackingId';

import { MOBILE_MENU_ID, RESPONSIVE_BP } from './constants';
import { MobileNavItem } from './MobileNavItem';

interface MobileMenuProps {
  isOpen: boolean;
  nav: Nav;
}

export function MobileMenu({ isOpen, nav }: MobileMenuProps) {
  const theme = useTheme();
  const { height } = useWindowSize();
  const { data: globals } = useGlobals();
  const secondaryPlpPages = globals?.siteSettings?.secondaryPlpMenu;

  return (
    <AnimatePresence>
      {isOpen && (
        <RemoveScroll>
          <MotionBox
            bgColor="gray.50"
            initial={{ x: '-100%' }}
            animate={{ x: '0%' }}
            exit={{ x: '-100%' }}
            transition={{
              duration: theme.transition.time.slow,
              type: 'tween',
            }}
            height={height}
            position="absolute"
            top={0}
            pt="var(--header-height)"
            left={0}
            width="100%"
            zIndex={0}
            display={{ base: 'block', [RESPONSIVE_BP]: 'none' }}
          >
            {/* The focus lock lives in Header, not here: it has to enclose the
             * close button and cart in the header bar as well as this panel. */}
            <Flex
              as="nav"
              id={MOBILE_MENU_ID}
              aria-label="Main menu"
              direction="column"
              overflowY="auto"
              pt="2xs"
              pb="sm"
              px="sm"
              height="100%"
            >
              {env.NEXT_PUBLIC_SITE_SEARCH_ENABLED && (
                <Hide above={RESPONSIVE_BP}>
                  <SiteSearchMobile />
                </Hide>
              )}
              {nav.primary.map((pn, i) => {
                switch (pn.type) {
                  case 'link':
                    return (
                      <MobileNavItem
                        key={i}
                        href={pn.href}
                        name={pn.name}
                        isExternal={pn.isExternal}
                        showIcon={pn.showIcon}
                      >
                        {pn.label}
                      </MobileNavItem>
                    );
                  case 'dropdown':
                    return (
                      <Accordion key={i} allowMultiple>
                        <AccordionItem
                          py="xs"
                          borderColor="blackAlpha.250"
                          borderBottomWidth={0}
                          _last={{ borderBottomWidth: 0 }}
                        >
                          {({ isExpanded }) => (
                            <>
                              <AccordionButton
                                className="mobile-menu_subnav-trigger"
                                p={0}
                                width="100%"
                                justifyContent="space-between"
                                _hover={{}}
                              >
                                <Text as="span" size="h3" textTransform="none">
                                  {pn.label}
                                </Text>
                                {isExpanded ? (
                                  <Minus boxSize="m" />
                                ) : (
                                  <Plus boxSize="m" />
                                )}
                              </AccordionButton>
                              <MotionAccordionPanel px="sm" pt="sm" pb="xs">
                                <Stack spacing="sm">
                                  <Stack spacing="3xs">
                                    {pn.subNav.items.map(
                                      (sn, j) =>
                                        sn.href && (
                                          <TextLink
                                            className="mobile-menu_nav-item"
                                            key={j}
                                            {...getExternalLinkProps(
                                              sn.isExternal,
                                              sn.href,
                                            )}
                                            {...getShowIconProps(sn.showIcon)}
                                            href={sn.href}
                                            id={getTrackingId(sn.name, {
                                              parent: 'header',
                                              platform: 'mobile',
                                            })}
                                            textProps={{
                                              size: 'h5',
                                              textTransform: 'none',
                                            }}
                                          >
                                            {sn.label}
                                          </TextLink>
                                        ),
                                    )}
                                  </Stack>
                                  {pn.subNav.contentType === 'products' &&
                                    secondaryPlpPages && (
                                      <SecondaryPlpNav
                                        secondaryPlpMenu={secondaryPlpPages}
                                      />
                                    )}
                                </Stack>
                              </MotionAccordionPanel>
                            </>
                          )}
                        </AccordionItem>
                      </Accordion>
                    );
                  default:
                    return null;
                }
              })}

              {nav.secondary.map((sn, i) => (
                <MobileNavItem
                  key={i}
                  href={sn.href}
                  name={sn.name}
                  isExternal={sn.isExternal}
                  onClick={sn.onClick}
                >
                  {sn.label}
                </MobileNavItem>
              ))}

              {nav.cta?.href && (
                <Button
                  as={NextLink}
                  href={nav.cta.href}
                  mt="3xs"
                  variant="outline"
                >
                  {nav.cta.label}
                </Button>
              )}

              {env.NEXT_PUBLIC_COUNTRY_SELECTOR_ENABLED && (
                <Box mt="auto" maxW="max-content">
                  <CountrySelector showFullName />
                </Box>
              )}
            </Flex>
          </MotionBox>
        </RemoveScroll>
      )}
    </AnimatePresence>
  );
}
